import { useEffect, useState } from "react";
import { ArrowUpRight, Menu, X, Instagram } from "lucide-react";
import { waLink, SOCIALS, LOGO_URL } from "@/lib/brand";

const NAV = [
  { label: "Verticals", href: "#verticals" },
  { label: "Services", href: "#services" },
  { label: "Reels", href: "#reels" },
  { label: "Work", href: "#work" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#0a0b08]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="brand-logo">
          <div className="relative w-11 h-11 rounded-xl bg-black border border-white/10 flex items-center justify-center overflow-hidden shadow-[0_0_25px_rgba(212,255,79,0.25)]">
            <img src={LOGO_URL} alt="Digi Savvy" className="w-full h-full object-contain p-1" />
            <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-[#d4ff4f] border-2 border-[#0a0b08] animate-pulse" />
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base sm:text-lg tracking-tight">DIGI SAVVY</span>
            <span className="text-[10px] text-white/50 font-mono uppercase tracking-[0.2em]">/ Real-Estate Marketing</span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="px-4 py-2 text-sm text-white/70 hover:text-[#d4ff4f] rounded-full hover:bg-white/5 transition"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={SOCIALS[0].href}
            target="_blank"
            rel="noreferrer"
            aria-label="Instagram"
            data-testid="header-instagram"
            className="hidden sm:inline-flex w-10 h-10 rounded-full border border-white/10 items-center justify-center text-white/70 hover:text-[#d4ff4f] hover:border-[#d4ff4f]/60 transition"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <a
            href={waLink("Hi Digi Savvy! I'd like to book a Growth Call.")}
            target="_blank"
            rel="noreferrer"
            data-testid="header-cta-growth-call"
            className="hidden sm:inline-flex items-center gap-2 bg-[#d4ff4f] text-black font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-white transition group"
          >
            Book a Growth Call
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="md:hidden w-10 h-10 rounded-full border border-white/10 flex items-center justify-center"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0a0b08]/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-base text-white/80 hover:text-[#d4ff4f] hover:bg-white/5 rounded-lg"
              >
                {n.label}
              </a>
            ))}
            <a
              href={waLink("Hi Digi Savvy, I'd like to book a Growth Call.")}
              target="_blank"
              rel="noreferrer"
              className="mt-2 bg-[#d4ff4f] text-black font-semibold px-5 py-3 rounded-full text-center"
            >
              Book a Growth Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
